import _ from 'lodash';

import {
  Load,
  LoadStopsItem,
  LoadStopsItemAddress,
  LoadStopsItemTypes,
  NormalizedLoad,
  normalizeLoad,
} from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { TMSOperation } from 'types/api/UpdateTMS';
import { TMS } from 'types/enums/Integrations';
import { normalizeTimezone } from 'utils/normalizeTimezone';
import { denormalizeDatesForTMSForm } from 'utils/parseDatesForTMSForm';

import {
  TRANSPORT_PRO_PRIORITY_NOTE_TYPE,
  TransportProAppointmentStatus,
} from './constants';
import {
  TransportProFormLoad,
  TransportProNote,
  TransportProSchedulingStop,
} from './types';

const NOT_REQUIRED_APPT_TYPES = new Set([
  TransportProAppointmentStatus.NotRequired,
  'first come first served',
  'not required',
]);

const API_APPT_TYPE_TO_FORM_STATUS: Record<
  string,
  TransportProAppointmentStatus
> = {
  appointment_required: TransportProAppointmentStatus.AppointmentRequired,
  'appointment required': TransportProAppointmentStatus.AppointmentRequired,
  requested: TransportProAppointmentStatus.Requested,
  confirmed: TransportProAppointmentStatus.Confirmed,
  denied: TransportProAppointmentStatus.Denied,
};

export function deriveAppointmentStatus(
  stop: Pick<LoadStopsItem, 'apptRequired' | 'apptType'>
): TransportProAppointmentStatus | '' {
  const normalizedType = (stop.apptType || '').toLowerCase().trim();

  if (NOT_REQUIRED_APPT_TYPES.has(normalizedType)) {
    return TransportProAppointmentStatus.NotRequired;
  }

  const mapped = API_APPT_TYPE_TO_FORM_STATUS[normalizedType];
  if (mapped) {
    return mapped;
  }

  if (stop.apptRequired === false) {
    return TransportProAppointmentStatus.NotRequired;
  }

  if (stop.apptRequired) {
    return TransportProAppointmentStatus.AppointmentRequired;
  }

  return '';
}

export function appointmentStatusToStopFields(status: string): {
  apptRequired: boolean;
  apptType: string;
} {
  switch (status) {
    case TransportProAppointmentStatus.NotRequired:
      return {
        apptRequired: false,
        apptType: TransportProAppointmentStatus.NotRequired,
      };
    case TransportProAppointmentStatus.AppointmentRequired:
      return {
        apptRequired: true,
        apptType: TransportProAppointmentStatus.AppointmentRequired,
      };
    case TransportProAppointmentStatus.Requested:
    case TransportProAppointmentStatus.Denied:
    case TransportProAppointmentStatus.Confirmed:
      return {
        apptRequired: true,
        apptType: status,
      };
    default:
      return { apptRequired: false, apptType: '' };
  }
}

export function normalizeStopAddress(
  address: Maybe<Partial<LoadStopsItemAddress>>
): LoadStopsItemAddress {
  return {
    name: address?.name ?? '',
    addressLine1: address?.addressLine1 ?? '',
    addressLine2: address?.addressLine2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    zip: address?.zip ?? '',
    timezone: address?.timezone ?? '',
    country: address?.country ?? '',
    isVerified: address?.isVerified ?? false,
  };
}

export function buildTransportProStops(
  load: Load
): TransportProSchedulingStop[] {
  const stops = load.stops?.length
    ? [...load.stops]
    : buildStopsFromPickupConsignee(load);

  return stops
    .sort((a, b) => (a.stopNumber ?? 0) - (b.stopNumber ?? 0))
    .map((stop, index) => {
      const address = normalizeStopAddress(stop.address);
      const timezone =
        normalizeTimezone(
          stop.timezone || address.timezone || load.pickup?.timezone
        ) || '';

      return {
        ...stop,
        stopNumber: stop.stopNumber ?? index,
        loadID: stop.loadID ?? load.ID ?? 0,
        stopType: normalizeStopType(stop.stopType),
        address: {
          ...address,
          timezone,
        },
        timezone,
        apptNote: stop.apptNote ?? '',
        apptStartTime: stop.apptStartTime ?? '',
        apptEndTime: stop.apptEndTime ?? '',
        apptRequired: stop.apptRequired ?? false,
        apptType: stop.apptType ?? '',
        appointmentStatus: deriveAppointmentStatus(stop),
      };
    });
}

function buildStopsFromPickupConsignee(load: Load): LoadStopsItem[] {
  const stops: LoadStopsItem[] = [];

  if (load.pickup?.name || load.pickup?.city) {
    stops.push(pickupConsigneeToStop(load, 'pickup', 0));
  }

  if (load.consignee?.name || load.consignee?.city) {
    stops.push(pickupConsigneeToStop(load, 'delivery', stops.length));
  }

  return stops;
}

function pickupConsigneeToStop(
  load: Load,
  stopType: 'pickup' | 'delivery',
  stopNumber: number
): LoadStopsItem {
  const isPickup = stopType === 'pickup';
  const location = (isPickup ? load.pickup : load.consignee) as
    | (typeof load.pickup & { apptType?: string })
    | (typeof load.consignee & { apptType?: string });

  return {
    loadID: load.ID ?? 0,
    stopType: isPickup
      ? LoadStopsItemTypes.Pickup
      : LoadStopsItemTypes.Delivery,
    stopNumber,
    address: normalizeStopAddress({
      name: location?.name,
      addressLine1: location?.addressLine1,
      addressLine2: location?.addressLine2,
      city: location?.city,
      state: location?.state,
      zip: location?.zipCode,
      country: location?.country,
    }),
    externalTMSID: location?.externalTMSID ?? '',
    contact: location?.contact ?? '',
    phone: location?.phone ?? '',
    email: location?.email ?? '',
    businessHours: location?.businessHours ?? '',
    refNumber: location?.refNumber ?? '',
    readyTime: isPickup ? (load.pickup?.readyTime ?? '') : '',
    readyEndTime: '',
    mustDeliver: isPickup ? '' : (load.consignee?.mustDeliver ?? ''),
    apptRequired: location?.apptRequired ?? false,
    apptType: location?.apptType ?? '',
    apptStartTime: location?.apptStartTime ?? '',
    apptEndTime: location?.apptEndTime ?? '',
    actualStartTime: '',
    actualEndTime: '',
    expectedStartTime: '',
    apptNote: location?.apptNote ?? '',
    timezone: location?.timezone ?? '',
    additionalReferences: null,
  };
}

function normalizeStopType(stopType: string): LoadStopsItemTypes {
  const value = (stopType || '').toLowerCase();
  if (
    value === 'pickup' ||
    value === 'pu' ||
    value === LoadStopsItemTypes.Pickup
  ) {
    return LoadStopsItemTypes.Pickup;
  }
  if (
    value === 'delivery' ||
    value === 'dropoff' ||
    value === LoadStopsItemTypes.Delivery
  ) {
    return LoadStopsItemTypes.Delivery;
  }
  return LoadStopsItemTypes.Stop;
}

export function buildTransportProLoadDefaults(load: Load): NormalizedLoad {
  const normalized = normalizeLoad(TMS.TransportPro, load);
  const normalizedLoad = {
    ...load,
    pickup: normalized.pickup,
    consignee: normalized.consignee,
    stops: normalized.stops,
  } as unknown as Load;

  return {
    ...normalized,
    externalTMSID: load.externalTMSID || load.freightTrackingID || '',
    status: load.status ?? '',
    customer: {
      ...normalized.customer,
      name: load.customer?.name ?? normalized.customer?.name ?? '',
      city: load.customer?.city ?? normalized.customer?.city ?? '',
      state: load.customer?.state ?? normalized.customer?.state ?? '',
    },
    stops: buildTransportProStops(normalizedLoad),
  } as NormalizedLoad;
}

export function getTransportProNoteText(
  note: Pick<TransportProNote, 'text' | 'note'>
): string {
  return (note.text ?? note.note ?? '').trim();
}

export function getTransportProIsPriorityNote(
  note: Pick<TransportProNote, 'noteType'>
): boolean {
  return note.noteType?.toLowerCase() === TRANSPORT_PRO_PRIORITY_NOTE_TYPE;
}

type StopWithWaypointID = LoadStopsItem & { externalTMSStopID?: string };

function getStopWaypointId(stop: Maybe<Partial<StopWithWaypointID>>): string {
  return stop?.externalTMSStopID ?? stop?.externalTMSID ?? '';
}

function mapFormStopForUpdate(
  stop: TransportProSchedulingStop,
  index: number,
  baseStop: Maybe<LoadStopsItem>
): StopWithWaypointID {
  const status = stop.appointmentStatus || deriveAppointmentStatus(stop);
  const { apptRequired, apptType } = appointmentStatusToStopFields(status);
  const baseWaypoint = getStopWaypointId(
    baseStop as Maybe<Partial<StopWithWaypointID>>
  );

  return {
    ...(baseStop ?? {}),
    ...stop,
    ...denormalizeDatesForTMSForm(TMS.TransportPro, {
      apptStartTime: stop.apptStartTime,
      apptEndTime: stop.apptEndTime,
      readyTime: stop.readyTime,
      mustDeliver: stop.mustDeliver,
    }),
    stopNumber: stop.stopNumber ?? index,
    apptRequired,
    apptType,
    externalTMSStopID:
      (stop as StopWithWaypointID).externalTMSStopID ??
      baseWaypoint ??
      stop.externalTMSID,
  };
}

function isPickupStop(stop: LoadStopsItem): boolean {
  const value = (stop.stopType || '').toLowerCase();
  return (
    value === 'pickup' || value === 'pu' || value === LoadStopsItemTypes.Pickup
  );
}

function isDeliveryStop(stop: LoadStopsItem): boolean {
  const value = (stop.stopType || '').toLowerCase();
  return (
    value === 'delivery' ||
    value === 'dropoff' ||
    value === LoadStopsItemTypes.Delivery
  );
}

function findBaseStopForUpdate(
  stop: TransportProSchedulingStop,
  baseStops: LoadStopsItem[]
): Maybe<LoadStopsItem> {
  const waypointId = getStopWaypointId(stop);
  if (waypointId) {
    const waypointMatch = baseStops.find(
      (baseStop) => getStopWaypointId(baseStop) === waypointId
    );
    if (waypointMatch) {
      return waypointMatch;
    }
  }

  const stopType = normalizeStopType(stop.stopType);
  return (
    baseStops.find(
      (baseStop) =>
        baseStop.stopNumber === stop.stopNumber &&
        normalizeStopType(baseStop.stopType) === stopType
    ) ?? null
  );
}

export function buildTransportProUpdateLoadRequest(
  load: Load,
  formValues: TransportProFormLoad
) {
  const tmsName = TMS.TransportPro;
  const baseStops = load.stops ?? [];

  const stops = (formValues.stops ?? []).map((stop, index) =>
    mapFormStopForUpdate(
      stop as TransportProSchedulingStop,
      index,
      findBaseStopForUpdate(stop as TransportProSchedulingStop, baseStops)
    )
  );

  const pickupStop = stops.find(isPickupStop);
  const deliveryStop = stops.find(isDeliveryStop);

  const pickupApptFields = pickupStop
    ? {
        apptStartTime: pickupStop.apptStartTime,
        apptEndTime: pickupStop.apptEndTime,
        apptType: pickupStop.apptType,
        apptRequired: pickupStop.apptRequired,
        externalTMSStopID:
          pickupStop.externalTMSStopID ?? pickupStop.externalTMSID,
      }
    : {};

  const consigneeApptFields = deliveryStop
    ? {
        apptStartTime: deliveryStop.apptStartTime,
        apptEndTime: deliveryStop.apptEndTime,
        apptType: deliveryStop.apptType,
        apptRequired: deliveryStop.apptRequired,
        externalTMSStopID:
          deliveryStop.externalTMSStopID ?? deliveryStop.externalTMSID,
      }
    : {};

  return {
    operation: TMSOperation.UpdateLoad,
    load: {
      ...load,
      freightTrackingID: formValues.freightTrackingID ?? load.freightTrackingID,
      externalTMSID: formValues.externalTMSID ?? load.externalTMSID,
      pickup: {
        ...load.pickup,
        ...denormalizeDatesForTMSForm(tmsName, pickupApptFields),
      },
      consignee: {
        ...load.consignee,
        ...denormalizeDatesForTMSForm(tmsName, consigneeApptFields),
      },
      stops,
    } as Load,
  };
}

export function formatStopLocationLabel(stop: LoadStopsItem): string {
  const city = stop.address?.city
    ? _.startCase(_.toLower(stop.address.city))
    : '';
  const state = stop.address?.state ?? '';
  const zip = stop.address?.zip ?? '';

  if (city && state && zip) {
    return `${city}, ${state} ${zip}`;
  }
  if (city && state) {
    return `${city}, ${state}`;
  }
  return city || state || zip || '—';
}

export function getStopTypeLabel(stopType: string): string {
  const value = (stopType || '').toLowerCase();
  if (value === 'pickup' || value === 'pu') {
    return 'Pickup';
  }
  if (value === 'delivery' || value === 'dropoff') {
    return 'Dropoff';
  }
  if (value === 'stopoff') {
    return 'Stopoff';
  }
  return _.startCase(value || 'Stop');
}
